<script>
import Draggable from "vuedraggable";
import DealCard from "./DealCard.vue";
import { mapGetters } from "vuex";

export default {
  name: "StageColumn",
  components: { Draggable, DealCard },
  props: {
    stage: { type: Object, required: true },
    totalStages: { type: Number, required: true },
    mobile: { type: Boolean, default: false },
  },
  data() {
    return {
      showStageMenu: false,
      hasModalOpen: false,
      pendingMove: null, // { dealId, targetStageId }
    };
  },
  computed: {
    ...mapGetters("crmBoard", [
      "columnTotals",
      "columnWeightedTotals",
      "filteredColumnDeals",
      "hasActiveFilters",
    ]),
    dealIds: {
      get() {
        return this.$store.state.crmBoard.dealsByStageId[this.stage.id] || [];
      },
      set(newIds) {
        this.$store.commit('crmBoard/SET_STAGE_DEALS', { stageId: this.stage.id, dealIds: newIds });
      },
    },
    filteredDealIds() {
      return new Set(this.filteredColumnDeals(this.stage.id).map((d) => d.id));
    },
    isColumnEmpty() {
      return this.filteredColumnDeals(this.stage.id).length === 0;
    },
    deals() {
      return this.$store.state.crmBoard.dealsById;
    },
    currentUser() {
      // Ajustá la ruta si en tu store está namespaced (p.ej. "session/getCurrentUser")
      return this.$store.getters.getCurrentUser || {};
    },
    totals() {
      return this.columnTotals(this.stage.id);
    },
    weightedTotals() {
      return this.columnWeightedTotals(this.stage.id);
    },
    isAdmin() {
      return this.currentUser?.role === "administrator";
    },
    canMoveLeft() {
      return this.stage.position > 1;
    },
    canMoveRight() {
      return this.stage.position < this.totalStages;
    },
    isDragDisabled() {
      return this.hasModalOpen;
    },
    isSortableDisabled() {
      return this.hasModalOpen || this.mobile;
    },
    stageMeta() {
      return this.$store.state.crmBoard.stageMeta[this.stage.id] || {};
    },
    isStageLoading() {
      return this.$store.state.crmBoard.stageIsLoading[this.stage.id] || false;
    },
    hasPagination() {
      return this.$store.state.crmBoard.isPaginated && (this.stageMeta.totalPages || 0) > 1;
    },
    currentPage() {
      return this.stageMeta.currentPage || 1;
    },
    totalPages() {
      return this.stageMeta.totalPages || 1;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeStageMenu();
      }
    },
    onStart(evt) {
      // Si hay modales abiertos, cancelar el drag
      if (this.hasModalOpen) {
        evt.preventDefault();
        return false;
      }
    },
    onChange(evt) {
      // Si hay un movimiento agregado a este stage (desde otro stage)
      if (evt.added) {
        const dealId = evt.added.element;
        const targetStageId = this.stage.id;

        // La decisión de si el stage destino requiere formulario la toma el
        // server (DealStageMover la consulta vía move sin form_data). Siempre
        // delegamos al padre, que orquesta el flujo completo y revierte el
        // drag optimista si corresponde.
        this.$emit("request-move", { dealId, targetStageId });
      }

      // Si hay un movimiento dentro del mismo stage (reordenamiento)
      if (evt.moved) {
        if (this.hasActiveFilters) return; // índices del subset filtrado no son confiables
        const dealId = evt.moved.element;
        // localPosition: índice relativo a la página visible (para REORDER_DEAL_LOCAL)
        const localPosition = evt.moved.newIndex + 1;
        // absolutePosition: posición global en el stage (para la API / ReorderService)
        // En paginación, la página 2 empieza en la posición 21 (con perPage=20), etc.
        const crmState = this.$store.state.crmBoard;
        const stageMeta = crmState.stageMeta[this.stage.id] || {};
        const pageOffset = crmState.isPaginated
          ? ((stageMeta.currentPage || 1) - 1) * (stageMeta.perPage || 20)
          : 0;
        const absolutePosition = pageOffset + localPosition;
        this.handleReorderDeal(dealId, localPosition, absolutePosition);
      }
    },
    onEnd() {
      // onChange maneja toda la lógica de movimientos
    },
    canMove(dealId) {
      const user = this.currentUser || {};
      const deal = this.deals[dealId];
      if (!deal || !user) {
        return false;
      }
      const canMove = !!(
        user.role === "administrator" || deal.owner_id === user.id
      );
      return canMove;
    },
    onMove(evt) {
      // draggedContext.element es el valor del array (tu dealId)
      const dealId = Number(evt.draggedContext?.element);
      return this.canMove(dealId);
    },

    async handleReorderDeal(dealId, localPosition, absolutePosition) {
      try {
        // Cambio local con posición relativa a la página (para el array visible)
        this.$store.commit("crmBoard/REORDER_DEAL_LOCAL", {
          dealId,
          newPosition: localPosition,
          stageId: this.stage.id,
        });

        // API recibe posición absoluta en el stage (ReorderService opera sobre todos los deals)
        await this.$store.dispatch("crmBoard/reorderDeal", {
          dealId,
          newPosition: absolutePosition,
        });
      } catch (error) {
        // En caso de error, re-fetch del stage para sincronizar
        const crmState = this.$store.state.crmBoard;
        const stageMeta = crmState.stageMeta[this.stage.id] || {};
        if (crmState.pipeline) {
          await this.$store.dispatch("crmBoard/fetchStage", {
            pipelineId: crmState.pipeline.id,
            stageId: this.stage.id,
            page: stageMeta.currentPage || 1,
          });
        }
      }
    },
    goToPage(page) {
      const pipelineId = this.$store.state.crmBoard.pipeline?.id;
      if (!pipelineId) return;
      this.$store.dispatch('crmBoard/fetchStage', {
        pipelineId,
        stageId: this.stage.id,
        page,
      });
    },
    toggleStageMenu() {
      this.showStageMenu = !this.showStageMenu;
    },
    closeStageMenu() {
      this.showStageMenu = false;
    },
    editStage() {
      this.$emit("edit-stage", this.stage);
      this.closeStageMenu();
    },
    deleteStage() {
      this.$emit("delete-stage", this.stage);
      this.closeStageMenu();
    },
    moveStageLeft() {
      this.$emit("move-stage-left", this.stage);
      this.closeStageMenu();
    },
    moveStageRight() {
      this.$emit("move-stage-right", this.stage);
      this.closeStageMenu();
    },
    onEditDeal(deal) {
      this.$emit("edit-deal", deal);
    },

    onCreateActivity(data) {
      this.$emit("create-activity", data);
    },

    onCompleteActivity(data) {
      this.$emit("complete-activity", data);
    },

    onDeleteActivity(data) {
      this.$emit("delete-activity", data);
    },

    onOpenActivityDetail(data) {
      this.$emit("open-activity-detail", data);
    },

    onModalOpen() {
      this.hasModalOpen = true;
    },

    onModalClose() {
      this.hasModalOpen = false;
    },
  },
};
</script>

<template>
  <div
    class="bg-white dark:bg-n-solid-2 rounded-lg shadow-sm border border-n-weak flex flex-col"
    :class="mobile ? 'w-full flex-shrink-0 h-full' : 'w-80 flex-shrink-0 max-h-[calc(100vh-300px)]'"
  >
    <!-- Header de la columna (fijo) -->
    <div class="flex-shrink-0 p-4 border-b border-n-weak">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-n-slate-12 truncate" :title="stage.name">{{ stage.name }}</h3>
        <div class="flex items-center space-x-2">
          <div
            class="bg-n-slate-9/10 text-n-slate-11 text-xs px-2 py-1 rounded-full"
          >
            {{ totals.dealCount }}
          </div>
          <div
            class="w-3 h-3 rounded-full"
            :style="{ backgroundColor: stage.color || '#6B7280' }"
          />

          <!-- Menú de tres puntos para stage (solo admin) -->
          <div v-if="isAdmin" class="relative">
            <button
              class="p-1 hover:bg-n-slate-9/10 rounded transition-all cursor-pointer border-0 outline-none"
              :class="{ 'bg-n-slate-9/10': showStageMenu }"
              @click="toggleStageMenu"
            >
              <svg
                class="w-4 h-4 text-n-slate-11"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                />
              </svg>
            </button>

            <!-- Dropdown del menú de stage -->
            <div
              v-if="showStageMenu"
              class="absolute right-0 top-8 bg-white dark:bg-n-solid-3 rounded-lg shadow-lg border border-n-weak z-50 min-w-[180px]"
            >
              <button
                class="w-full text-left px-4 py-2 text-sm text-n-slate-12 hover:bg-n-slate-9/5 transition-all cursor-pointer border-0 outline-none rounded-t-lg"
                @click="editStage"
              >
                <svg
                  class="w-4 h-4 inline mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Modificar Etapa
              </button>
              <button
                :disabled="!canMoveLeft"
                class="w-full text-left px-4 py-2 text-sm transition-all cursor-pointer border-0 outline-none flex items-center"
                :class="{
                  'text-n-slate-12 hover:bg-n-slate-9/5': canMoveLeft,
                  'text-n-slate-7 cursor-not-allowed': !canMoveLeft,
                }"
                @click="moveStageLeft"
              >
                <svg
                  class="w-4 h-4 inline mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Mover Izquierda
              </button>
              <button
                :disabled="!canMoveRight"
                class="w-full text-left px-4 py-2 text-sm transition-all cursor-pointer border-0 outline-none flex items-center"
                :class="{
                  'text-n-slate-12 hover:bg-n-slate-9/5': canMoveRight,
                  'text-n-slate-7 cursor-not-allowed': !canMoveRight,
                }"
                @click="moveStageRight"
              >
                <svg
                  class="w-4 h-4 inline mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Mover Derecha
              </button>
              <button
                class="w-full text-left px-4 py-2 text-sm text-n-ruby-10 hover:bg-n-ruby-10/5 transition-all cursor-pointer border-0 outline-none rounded-b-lg"
                @click="deleteStage"
              >
                <svg
                  class="w-4 h-4 inline mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Eliminar Etapa
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Área de deals con scroll interno -->
    <div
      class="flex-1 overflow-y-auto p-4 min-h-0 relative"
      style="scrollbar-width: thin"
    >
      <!-- Overlay semitransparente durante carga de página -->
      <div
        v-if="isStageLoading"
        class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-n-solid-2/60 rounded"
      >
        <div class="w-5 h-5 border-2 border-n-brand border-t-transparent rounded-full animate-spin" />
      </div>
      <div class="min-h-[200px]">
        <Draggable
          v-model="dealIds"
          group="deals"
          :item-key="(item) => item"
          class="space-y-3 min-h-[50px]"
          :class="[{ 'drag-disabled': isDragDisabled }]"
          :data-stage-id="stage.id"
          :move="onMove"
          ghost-class="ghost"
          chosen-class="chosen"
          drag-class="drag"
          :force-fallback="false"
          :disabled="isSortableDisabled"
          tag="div"
          @start="onStart"
          @end="onEnd"
          @change="onChange"
        >
          <template #item="{ element }">
            <DealCard
              v-if="filteredDealIds.has(element)"
              :key="element"
              :deal="deals[element]"
              :mobile="mobile"
              @edit-deal="onEditDeal"
              @create-activity="onCreateActivity"
              @complete-activity="onCompleteActivity"
              @delete-activity="onDeleteActivity"
              @open-activity-detail="onOpenActivityDetail"
            />
          </template>
        </Draggable>

        <!-- Área de drop vacía -->
        <div
          v-if="isColumnEmpty"
          class="text-center py-8 text-n-slate-11 text-sm border-2 border-dashed border-n-weak rounded-lg"
        >
          <svg
            class="w-8 h-8 mx-auto mb-2 text-n-slate-9"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Arrastra deals aquí
        </div>
      </div>
    </div>

    <!-- Paginación por stage (visible solo cuando hay más de 1 página) -->
    <div
      v-if="hasPagination"
      class="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-n-weak bg-n-slate-9/5 dark:bg-n-solid-1"
    >
      <button
        :disabled="currentPage === 1 || isStageLoading"
        class="w-7 h-7 flex items-center justify-center rounded transition-colors border-0 outline-none"
        :class="currentPage === 1 || isStageLoading
          ? 'text-n-slate-7 cursor-not-allowed'
          : 'text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-slate-12 cursor-pointer'"
        @click="goToPage(currentPage - 1)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <span class="text-xs text-n-slate-11 select-none">
        Pág. {{ currentPage }} de {{ totalPages }}
      </span>

      <button
        :disabled="currentPage === totalPages || isStageLoading"
        class="w-7 h-7 flex items-center justify-center rounded transition-colors border-0 outline-none"
        :class="currentPage === totalPages || isStageLoading
          ? 'text-n-slate-7 cursor-not-allowed'
          : 'text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-slate-12 cursor-pointer'"
        @click="goToPage(currentPage + 1)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Footer con totales (fijo) -->
    <div
      class="flex-shrink-0 p-4 border-t border-n-weak bg-n-slate-9/5 dark:bg-n-solid-1"
    >
      <div class="space-y-2">
        <!-- Total normal -->
        <div
          v-if="Object.keys(totals.sumByCurrency).length > 0"
          class="text-sm font-medium text-n-slate-12"
        >
          <div
            v-for="(sum, currency) in totals.sumByCurrency"
            :key="currency"
            class="flex justify-between"
          >
            <span>Total {{ currency }}:</span>
            <span class="font-semibold">{{
              (sum / 100).toLocaleString()
            }}</span>
          </div>
        </div>

        <!-- Total ponderado -->
        <div
          v-if="Object.keys(weightedTotals.weightedSumByCurrency).length > 0"
          class="text-sm font-medium text-n-brand-10"
        >
          <div
            v-for="(sum, currency) in weightedTotals.weightedSumByCurrency"
            :key="'weighted-' + currency"
            class="flex justify-between"
          >
            <span>Total ponderado:</span>
            <span class="font-semibold">{{
              (sum / 100).toLocaleString()
            }}</span>
          </div>
        </div>

        <!-- Contador de deals y leads -->
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>{{ totals.dealCount }} deal{{
              totals.dealCount !== 1 ? "s" : ""
            }}</span>
          <span>{{ totals.uniqueLeadCount }} lead{{
              totals.uniqueLeadCount !== 1 ? "s" : ""
            }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ghost {
  opacity: 0.3;
  background: rgb(var(--color-n-brand) / 0.1);
  border: 2px dashed rgb(var(--color-n-brand) / 0.3);
  transform: rotate(2deg);
}
.chosen {
  opacity: 0.9;
  transform: scale(1.02);
  z-index: 10;
}
.drag {
  opacity: 0.7;
  transform: rotate(-2deg) scale(1.05);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 20;
}
.drag-disabled {
  opacity: 0.6;
}
.drag-disabled > * {
  cursor: not-allowed !important;
}
/* Solo deshabilitar pointer-events en los deal cards, no en los modales */
.drag-disabled :deep(.p-3.bg-white) {
  pointer-events: none;
}
/* Los modales (con clase fixed) siguen funcionando */
.drag-disabled :deep(.fixed.inset-0) {
  pointer-events: auto !important;
  opacity: 1 !important;
}
.drag-disabled :deep(.fixed.inset-0 *) {
  pointer-events: auto !important;
  cursor: auto !important;
  opacity: 1 !important;
}
</style>
